import { useMemo, useState } from 'react';
import Nr2jProspectRowMenu, {
  NR2J_KANBAN_STAGES,
  formatProspectNextAction,
  kanbanColumnForStage,
} from './Nr2jProspectRowMenu.jsx';
import Nr2jOwnerCell from './Nr2jOwnerCell.jsx';
import { contactDisplayName, organizationDisplayName } from '../../utils/nr2jConstants';

/**
 * Kanban pipeline NR2J — drag & drop HTML5 natif (pas de lib).
 * Colonnes actives uniquement ; GAGNE/PERDU hors board.
 */
export default function Nr2jProspectionKanban({
  organizations = [],
  menuId,
  onMenuToggle,
  onMenuClose,
  menuActionsFor,
  onOpenOrganization,
  onMoveStage,
  busyId = null,
  showOwner = false,
  ownerLinkable = false,
  viewerId = null,
}) {
  const [draggingId, setDraggingId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);

  const byColumn = useMemo(() => {
    const map = Object.fromEntries(NR2J_KANBAN_STAGES.map((c) => [c.id, []]));
    for (const org of organizations) {
      const col = kanbanColumnForStage(org.pipelineStage);
      if (col && map[col]) map[col].push(org);
    }
    return map;
  }, [organizations]);

  function handleDrop(targetStage) {
    const orgId = draggingId;
    setDraggingId(null);
    setOverColumn(null);
    if (!orgId || !targetStage) return;
    const org = organizations.find((o) => o.id === orgId);
    if (!org) return;
    const fromCol = kanbanColumnForStage(org.pipelineStage);
    if (fromCol === targetStage) return;
    onMoveStage?.(org, targetStage);
  }

  return (
    <div
      className="flex gap-3 overflow-x-auto pb-2"
      data-testid="nr2j-prospection-kanban"
    >
      {NR2J_KANBAN_STAGES.map((col) => {
        const cards = byColumn[col.id] || [];
        const isOver = overColumn === col.id;
        return (
          <div
            key={col.id}
            data-testid={`nr2j-kanban-col-${col.id}`}
            className={`flex w-[240px] shrink-0 flex-col rounded-xl border bg-gray-50/80 ${
              isOver ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-gray-200'
            }`}
            onDragOver={(e) => {
              e.preventDefault();
              setOverColumn(col.id);
            }}
            onDragLeave={() => {
              setOverColumn((cur) => (cur === col.id ? null : cur));
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDrop(col.id);
            }}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                {col.label}
              </h3>
              <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-gray-500 ring-1 ring-gray-200">
                {cards.length}
              </span>
            </div>
            <div className="flex min-h-[120px] flex-col gap-2 p-2">
              {cards.map((org) => {
                const next = formatProspectNextAction(org);
                const contact = org.primaryContact
                  ? contactDisplayName(org.primaryContact)
                    || org.primaryContact.email
                  : (org.emailPrincipal || null);
                const isBusy = busyId === org.id;
                return (
                  <div
                    key={org.id}
                    draggable={!isBusy}
                    onDragStart={(e) => {
                      setDraggingId(org.id);
                      e.dataTransfer.setData('text/plain', org.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setOverColumn(null);
                    }}
                    className={`rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm ${
                      draggingId === org.id ? 'opacity-50' : ''
                    } ${isBusy ? 'pointer-events-none opacity-60' : 'cursor-grab active:cursor-grabbing'}`}
                    data-testid={`nr2j-kanban-card-${org.id}`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => onOpenOrganization?.(org.id)}
                      >
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {organizationDisplayName(org)}
                        </p>
                        <p className="truncate text-xs text-gray-500">{org.ville || '—'}</p>
                      </button>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Nr2jProspectRowMenu
                          org={org}
                          open={menuId === org.id}
                          onToggle={() => onMenuToggle?.(org.id)}
                          onClose={onMenuClose}
                          actions={menuActionsFor?.(org) || []}
                        />
                      </div>
                    </div>
                    {showOwner ? (
                      <div className="mt-1.5 min-w-0" data-testid={`nr2j-kanban-owner-${org.id}`}>
                        <Nr2jOwnerCell
                          owner={org.ownerUser}
                          linkable={ownerLinkable}
                          viewerId={viewerId}
                          compact
                        />
                      </div>
                    ) : null}
                    {contact ? (
                      <p className="mt-1 truncate text-[11px] text-gray-500">{contact}</p>
                    ) : null}
                    <p
                      className={`mt-1.5 text-[11px] ${
                        next.overdue ? 'font-medium text-rose-700' : 'text-gray-600'
                      }`}
                    >
                      {next.text}
                    </p>
                    {(org.openTaskCount ?? 0) > 0 && next.kind !== 'task' ? (
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        {org.openTaskCount} tâche{(org.openTaskCount || 0) > 1 ? 's' : ''}
                      </p>
                    ) : null}
                  </div>
                );
              })}
              {cards.length === 0 ? (
                <p className="px-1 py-4 text-center text-[11px] text-gray-400">Aucun prospect</p>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
